import React from 'react';
import { Award, ShieldCheck, MapPin, Users } from 'lucide-react';
import { stats } from '../data/mock';
import { useTranslation } from '../hooks/useTranslation';

export const About = () => {
  const { t } = useTranslation();
  
  const iconMap = {
    'award': Award,
    'shield-check': ShieldCheck,
    'map-pin': MapPin,
    'users': Users,
    'calendar': Award,
    'file-check': ShieldCheck,
    'heart': Users
  };

  return (
    <section id="about" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-2 bg-burgundy-100 text-burgundy-700 rounded-full text-sm font-semibold mb-4">
            {t.about.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-6">
            {t.about.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t.about.description}
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {t.about.stats.map((stat, index) => {
            const Icon = iconMap[stats[index].icon] || Award;
            return (
              <div 
                key={index}
                className="bg-gradient-to-br from-navy-50 to-burgundy-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 border border-navy-100"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-burgundy-700 rounded-full mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-4xl font-bold text-navy-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Why Choose Us */}
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-navy-900 text-center mb-12">
            {t.about.whyChooseTitle}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.about.whyChoose.map((item, index) => {
              const iconName = ['award', 'shield-check', 'map-pin', 'users'][index];
              const Icon = iconMap[iconName] || Award;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-burgundy-300 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-burgundy-600 to-burgundy-800 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-navy-900 mb-3">{item.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Professional Credentials Notice */}
        <div className="max-w-4xl mx-auto mt-16 bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-burgundy-600 rounded-full mb-4">
            <Award className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">
            {t.about.certifiedTitle}
          </h3>
          <p className="text-white/80 text-lg">
            {t.about.certifiedDesc}
          </p>
        </div>
      </div>
    </section>
  );
};
